import { createStorefrontApiClient } from '@shopify/storefront-api-client';
import { fetchCognitoUserToken } from '../utils/user';

const backendURL = process.env.REACT_APP_BACKEND_URL;
const storeDomain = process.env.REACT_APP_SHOPIFY_STORE_DOMAIN;
const shopifyAccessToken = process.env.REACT_APP_SHOPIFY_STOREFRONT_API_TOKEN;
const apiVersion = process.env.REACT_APP_SHOPIFY_API_VERSION;
const productVariantId = process.env.REACT_APP_SHOPIFY_PRODUCT_VARIANT_ID;

export const sClient = createStorefrontApiClient({
  storeDomain: storeDomain,
  apiVersion: apiVersion,
  publicAccessToken: shopifyAccessToken,
});

const productQuery = `
  query getProductById($handle: String) {
    product(handle: $handle) {
      id
      handle
      title
      description
      variants(first: 1) {
        edges {
          node {
            id
            compareAtPrice {
              amount
            }
            price {
              amount
            }
          }
        }
      }
      featuredImage {
        url
      }
    }
  }
`;

export async function getProduct(handle) {
  const { data, errors, extensions } = await sClient.request(productQuery, {
    variables: {
      handle: handle,
    },
  });

  return data.product;
}

export function formatPrice(amount) {
  const price = Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(Number(amount));
  return price;
}

export function getProductPrice(sProduct) {
  const productPrice = formatPrice(sProduct.variants.edges[0].node.price.amount);
  return productPrice;
}

export function getProductCompareAtPrice(sProduct) {
  if (sProduct.variants.edges[0].node.compareAtPrice) {
    const productPrice = formatPrice(sProduct.variants.edges[0].node.compareAtPrice.amount);
    return productPrice;
  } else {
    return null;
  }
}

const createCartQuery = `
mutation createCart($lines: [CartLineInput!]!) {
  cartCreate(
    input: { lines: $lines }
  ) {
    cart {
      id
      createdAt
      updatedAt
      lines(first: 10) {
        edges {
          node {
            id
            merchandise {
              ... on ProductVariant {
                id
              }
            }
          }
        }
      }
      buyerIdentity {
        deliveryAddressPreferences {
          __typename
        }
        preferences {
          delivery {
            deliveryMethod
          }
        }
      }
      attributes {
        key
        value
      }
      # The estimated total cost of all merchandise that the customer will pay at checkout.
      cost {
        totalAmount {
          amount
          currencyCode
        }
        # The estimated amount, before taxes and discounts, for the customer to pay at checkout.
        subtotalAmount {
          amount
          currencyCode
        }
        # The estimated tax amount for the customer to pay at checkout.
        totalTaxAmount {
          amount
          currencyCode
        }
        # The estimated duty amount for the customer to pay at checkout.
        totalDutyAmount {
          amount
          currencyCode
        }
      }
    }
  }
}
`;

export async function createCart(cartData) {
  const { data, errors, extensions } = await sClient.request(createCartQuery, {
    variables: {
      lines: [
        {
          quantity: 1,
          merchandiseId: 'gid://shopify/ProductVariant/' + productVariantId,
        },
      ],
    },
  });

  const cartId = data.cartCreate.cart.id;
  localStorage.setItem('cart', cartId);

  await createApiCart(cartId, data.cartCreate.cart, cartData);

  return cartId;
}

export async function createApiCart(cartId, sCart, cartData) {
  let accessToken = await fetchCognitoUserToken();
  const shopCart = {
    id: cartId,
    data: JSON.stringify(cartData),
    s_data: JSON.stringify(sCart),
    checkout_url: '',
  };

  let headers = {
    'Content-Type': 'application/json',
    'x-shop-key': shopifyAccessToken,
  };

  if (accessToken) {
    headers['Authorization'] = `Bearer ${accessToken}`;
  }

  fetch(`${backendURL}/shop/cart`, {
    method: 'POST',
    headers: headers,
    body: JSON.stringify(shopCart),
  })
    .then((response) => {
      if (!response.ok) {
        console.error('Error creating cart.');
      }
    })
    .catch((error) => {
      return { error: 'Failed to create cart. Please try again.' };
    });
}

const checkoutUrlQuery = `
query checkoutURL($id: ID!) {
  cart(id: $id) {
    checkoutUrl
  }
}
`;

export async function getCheckoutUrl(cartId) {
  const { data, errors, extensions } = await sClient.request(checkoutUrlQuery, {
    variables: {
      id: cartId,
    },
  });

  if (data.cart) {
    return data.cart.checkoutUrl;
  } else {
    return null;
  }
}

const getProductMediaQuery = `
query getProductMediaByHandle($handle: String!) {
  product(handle: $handle) {
    media(first: 15) {
      edges {
        node {
          id
          mediaContentType
          previewImage {
            url
            width
            height
          }
          ... on Video {
            sources {
              url
              mimeType
            }
          }
          ... on ExternalVideo {
            embedUrl
          }
        }
      }
    }
  }
}`;

export async function getProductMedia(productHandleName) {
  const { data, errors, extensions } = await sClient.request(getProductMediaQuery, {
    variables: {
      handle: productHandleName,
    },
  });
  return 'product' in data ? data.product?.media?.edges : [];
}

const getCartQuery = `
query getCartById($cartId: ID!) {
  cart(
    id: $cartId
  ) {
    id
    createdAt
    updatedAt
    lines(first: 10) {
      edges {
        node {
          id
          quantity
          merchandise {
            ... on ProductVariant {
              id
            }
          }
          attributes {
            key
            value
          }
        }
      }
    }
    attributes {
      key
      value
    }
    cost {
      totalAmount {
        amount
        currencyCode
      }
      subtotalAmount {
        amount
        currencyCode
      }
      totalTaxAmount {
        amount
        currencyCode
      }
      totalDutyAmount {
        amount
        currencyCode
      }
    }
    buyerIdentity {
      email
      phone
      customer {
        id
      }
      countryCode
      deliveryAddressPreferences {
        ... on MailingAddress {
          address1
          address2
          city
          provinceCode
          countryCodeV2
          zip
        }
      }
      preferences {
        delivery {
          deliveryMethod
        }
      }
    }
  }
}
`;

export async function getCart(cartId) {
  const { data, errors, extensions } = await sClient.request(getCartQuery, {
    variables: {
      cartId: cartId,
    },
  });

  return 'cart' in data ? data.cart : null;
}

const cartLinesUpdateQuery = `
mutation updateCartLines($cartId: ID!, $lines: [CartLineUpdateInput!]!) {
  cartLinesUpdate(
    cartId: $cartId,
    lines: $lines
  ) {
    cart {
      id
      lines(first: 10) {
        edges {
          node {
            id
            quantity
            merchandise {
              ... on ProductVariant {
                id
              }
            }
          }
        }
      }
      cost {
        totalAmount {
          amount
          currencyCode
        }
        subtotalAmount {
          amount
          currencyCode
        }
        totalTaxAmount {
          amount
          currencyCode
        }
        totalDutyAmount {
          amount
          currencyCode
        }
      }
    }
  }
}
`;

export async function updateCartQuantity(cartId, quantity, cartData) {
  if (!cartId || cartId.length === 0) {
    return null;
  }

  const cart = await getCart(cartId);

  const lineId = cart.lines.edges[0].node.id;

  const { data, errors, extensions } = await sClient.request(cartLinesUpdateQuery, {
    variables: {
      cartId: cartId,
      lines: [
        {
          id: lineId,
          quantity: quantity,
        },
      ],
    },
  });

  updateApiCart(cartId, '', cartData);

  return data;
}

const cartBuyerIdentityUpdateQuery = `
mutation updateCartBuyerIdentity($cartId: ID!, $buyerIdentity: CartBuyerIdentityInput!) {
  cartBuyerIdentityUpdate(
    cartId: $cartId
    buyerIdentity: $buyerIdentity
  ) {
    cart {
      id
      buyerIdentity {
        email
        phone
        countryCode
        deliveryAddressPreferences
        preferences {
          delivery {
            deliveryMethod
            pickupHandle
          }
        }
      }
    }
  }
}
`;

export async function updateCartBuyer(cartId, buyerIdentity) {
  const { data, errors, extensions } = await sClient.request(cartBuyerIdentityUpdateQuery, {
    variables: {
      cartId: cartId,
      buyerIdentity: buyerIdentity,
    },
  });

  return data;
}

// Under the hood it uses the same updateApiCart function
// plus some callbacks needed for the AddressCheckoutForm / OrderSubjectsForm
// and the main OrderPFAS component
export async function updateApiCartWithCallbacks(
  cartId,
  shopifyCheckoutUrl,
  cartData,
  successCallback = () => {},
  failureCallback = () => {},
) {
  updateApiCart(cartId, shopifyCheckoutUrl, cartData)
    .then((response) => {
      // if (!response.ok) {
      //   // Should we run a different callback
      //   // and then based on that show the retry button/link within the AddressCheckoutForm
      //   // so the user can re-try again
      // }
      successCallback();
      return response;
    })
    .catch((error) => {
      failureCallback();
      console.error('catch Exception response of updateApiCartWithCallbacks:', error);
    });
}

export async function updateApiCart(cartId, shopifyCheckoutUrl, cartData) {
  let accessToken = await fetchCognitoUserToken();
  const cart = await getCart(cartId);

  const shopCart = {
    id: cartId,
    data: JSON.stringify(cartData),
    s_data: JSON.stringify(cart),
    checkout_url: shopifyCheckoutUrl,
  };

  let headers = {
    'Content-Type': 'application/json',
    'x-shop-key': shopifyAccessToken,
  };

  if (accessToken) {
    headers['Authorization'] = `Bearer ${accessToken}`;
  }

  return fetch(`${backendURL}/shop/cart`, {
    method: 'PATCH',
    headers: headers,
    body: JSON.stringify(shopCart),
  })
    .then((response) => {
      if (!response.ok) {
        console.error('Error updating cart.');
      }
      return response;
    })
    .catch((error) => {
      return { error: 'Failed to update. Please try again.' };
    });
}

export async function getExternalOrder(cartId) {
  let accessToken = await fetchCognitoUserToken();
  const shopCart = {
    id: cartId,
    data: '',
    s_data: '',
    checkout_url: '',
  };

  let headers = {
    'Content-Type': 'application/json',
    'x-shop-key': shopifyAccessToken,
  };

  if (accessToken) {
    headers['Authorization'] = `Bearer ${accessToken}`;
  }

  let orderName = null;

  const response = await fetch(`${backendURL}/shop/external-order`, {
    method: 'PATCH',
    headers: headers,
    body: JSON.stringify(shopCart),
  });

  if (response.ok) {
    let externalOrder = await response.json();
    orderName = externalOrder.s_order_name;
  }
  return orderName;
}

export async function handlePlaceOrder(cart, selectedUsers, address) {
  let accessToken = await fetchCognitoUserToken();
  const orderData = {
    s_cart_id: cart,
    total_quantity: selectedUsers.length,
    shipping_address: address,
    test_subjects: selectedUsers,
  };

  let headers = {
    'Content-Type': 'application/json',
    'x-shop-key': shopifyAccessToken,
  };

  if (accessToken) {
    headers['Authorization'] = `Bearer ${accessToken}`;
  }

  let orderId = null;
  const response = await fetch(`${backendURL}/shop/order`, {
    method: 'POST',
    headers: headers,
    body: JSON.stringify(orderData),
  });

  if (response.ok) {
    let createOrderResponse = await response.json();
    orderId = createOrderResponse.s_order_name;
  } else {
    console.error('Error while placing order.');
  }
  return orderId;
}
